import React, { useState } from 'react';
import { Users, FileText, Link, Home, LogOut, Upload, User } from 'lucide-react';
import ContentManager from './ContentManager';
import AgentManager from './AgentManager';
import LinkManager from './LinkManager';
import { AccessIdentity } from '../../hooks/useAccessIdentity';
import { adminLog, adminLogError } from '../../lib/adminLog';

interface AdminDashboardProps {
  user: AccessIdentity;
  onLogout?: () => void;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('content');
  const [isApplying, setIsApplying] = useState(false);
  const [applyMessage, setApplyMessage] = useState('');

  const tabs = [
    { id: 'content', label: 'Website Text', icon: <FileText size={20} /> },
    { id: 'agents', label: 'Team Members', icon: <Users size={20} /> },
    { id: 'links', label: 'Contact & Links', icon: <Link size={20} /> },
  ];

  const handleApplyChanges = async () => {
    setIsApplying(true);
    setApplyMessage('');
    adminLog('publish.started');
    try {
      const response = await fetch('/api/admin/apply-changes', { method: 'POST' });
      const result = await response.json().catch(() => null);
      if (!response.ok) throw new Error(result?.error || 'The website update could not be started.');
      adminLog('publish.queued');
      setApplyMessage('Your changes are being added to the website. They should appear within a few minutes.');
    } catch (error) {
      adminLogError('publish.failed', error);
      setApplyMessage('We could not update the website. Please try again. If the problem continues, contact Kyle at kylemirich20@gmail.com.');
    } finally {
      setIsApplying(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'content':
        return <ContentManager />;
      case 'agents':
        return <AgentManager />;
      case 'links':
        return <LinkManager />;
      default:
        return <ContentManager />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-bbha-cream-50 to-bbha-olive-50">
      {/* Admin Header */}
      <header className="bg-gradient-to-r from-bbha-plum-600 to-bbha-plum-700 shadow-lg border-b border-bbha-plum-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <img 
                src="/bhba-logo.png" 
                alt="BBHA Insurance Services" 
                className="h-12 w-auto"
              />
              <div className="border-l border-white/30 pl-4">
                <h1 className="text-2xl font-bold text-white">Website Editor</h1>
                <p className="text-sm text-white/80">Update your website</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* User Info */}
              {user && (
                <div className="flex items-center space-x-3 text-white/80">
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <User size={16} />
                  </div>
                  <span className="text-sm font-medium">
                    {user.email}
                  </span>
                </div>
              )}
              
              <a 
                href="/" 
                className="flex items-center space-x-2 text-white/80 hover:text-white hover:bg-white/20 px-3 py-2 rounded-lg transition-colors duration-200"
              >
                <Home size={20} />
                <span>View Site</span>
              </a>
              {onLogout && (
                <button
                  onClick={onLogout}
                  className="flex items-center space-x-2 bg-white/20 text-white px-4 py-2 rounded-lg hover:bg-white/30 transition-colors duration-200"
                >
                  <LogOut size={16} />
                  <span>Sign Out</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors duration-200 ${
                  activeTab === tab.id
                    ? 'border-bbha-olive-500 text-bbha-olive-600'
                    : 'border-transparent text-gray-500 hover:text-bbha-plum-600 hover:border-bbha-plum-300'
                }`}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 min-h-[600px]">
          {renderTabContent()}
        </div>
        
        <div className="mt-8 bg-gradient-to-r from-bbha-olive-50 to-bbha-cream-50 rounded-lg p-6 border border-bbha-olive-200">
          <div className="flex items-center justify-between gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Make Your Changes Live</h3>
              <p className="text-gray-600 text-sm">When you are ready, update the website. Your changes should appear within a few minutes.</p>
              {applyMessage && <p className="mt-2 text-sm text-bbha-plum-700">{applyMessage}</p>}
            </div>
            <button
              onClick={handleApplyChanges}
              disabled={isApplying}
              className="flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold bg-gradient-to-r from-bbha-olive-600 to-bbha-olive-700 text-white disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <Upload className="w-5 h-5" />
              <span>{isApplying ? 'Starting Update...' : 'Update Website'}</span>
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
